import { colors, sxButton, sxFont_16_400, sxFont_18_500 } from "@/theme";
import {
  formatAxiosError,
  isFunction,
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString
} from "@/utils";
import {
  Add,
  SaveAlt
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRowModesModel,
  GridRowsProp,
  GridSlotProps,
  GridToolbarContainer
} from "@mui/x-data-grid";
import {
  forwardRef,
  useImperativeHandle
} from "react";
import AlertDialog from "../AlertDialog";
import { getColumnActions } from "./columnActions";
import { DataGridDefaultStyle, pageSize } from "./constants";
import { CrudGridProps, CrudGridRef } from "./interface";
import * as S from "./styles";
import useCrudGrid from "./useCrudGrid";
import useEditToolBar from "./useEditToolBar";
import { UploadFileInput } from "@/components/atoms";


declare module "@mui/x-data-grid" {
  interface ToolbarPropsOverrides {
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (
      newModel: (oldModel: GridRowModesModel) => GridRowModesModel
    ) => void;
    newRecord?: any;
    fieldToFocus?: string;
    newRecordId: number;
    exportFn?: () => void;
  }
}


function EditToolbar(props: GridSlotProps["toolbar"]) {
  const editorTools = useEditToolBar(props)
  if (!editorTools) return
  const { handleAddRecordClick, handleExportClick, exportFn, newRecord } = editorTools

  return (
    <GridToolbarContainer>
      {isNonEmptyObject(newRecord) ? (
        <Button
          color="primary"
          startIcon={<Add />}
          onClick={handleAddRecordClick}
        >
          Add record
        </Button>
      ) : null}
      {isFunction(exportFn) ? (
        <Button
          color="primary"
          startIcon={<SaveAlt />}
          onClick={handleExportClick}
        >
          Export
        </Button>
      ) : null}
    </GridToolbarContainer>
  );
}

const CrudGrid = forwardRef<CrudGridRef, CrudGridProps>(
  (
    props,
    ref
  ) => {
    const {
      baseUrl,
      path,
      additionalURLSearchParams,
      columns,
      getRowId,
      setRowId,
      header,
      newRecord,
      fieldToFocus,
      additionalAction,
      newRecordId = -1,
      exportFn,
      initialState,
      onRowClick,
      search: _search,
      // Optional outward-facing callback for parents that need to react to
      // search changes (e.g. forwarding the term to a server-side export).
      // The grid still manages its own internal `searchInput` state via the
      // useCrudGrid hook — this just mirrors the latest value back out.
      setSearch,
      onEditRow,
      setTotalRowCount: _setTotalRowCount,
      withoutActionColumn,
      checkboxSelection,
      rowDropdownIcon,
      searchPlaceHolder,
      shouldShowDelete = true,
      bulkImportButton,
      exportButton,
      filtersButton,
      hideSearchBar
    } = props

    console.log(shouldShowDelete)

    const { rowModesModel, rows, handleSaveClick,
      handleCancelClick,
      handleEditClick,
      handleDeleteClick,
      totalRowCount,
      isDeleteDialogOpen,
      searchInput,
      setSearchInput,
      onPaginationModelChange,
      onFilterModelChange,
      onSortModelChange,
      processRowUpdate,
      handleRowModesModelChange,
      handleRowEditStop,
      handleDeleteDialogClosed,
      reloadData,
      deleteSelectedRows,
      getSelectedRowIds,
      onSearchChange,
      apiRef,
      isLoading,
      paginationModel,
      setRows,
      setRowModesModel,
      error
    } = useCrudGrid(props)

    const columnActions = getColumnActions({
      _setTotalRowCount, rowModesModel, rows, handleSaveClick,
      handleCancelClick,
      handleEditClick,
      checkboxSelection,
      handleDeleteClick,
      additionalAction,
      shouldShowDelete
    })
    // Ensure all columns have disableColumnMenu: true
    const columnsWithDisableMenu = (columns || []).map(col => ({
      ...col,
      disableColumnMenu: col.disableColumnMenu !== undefined ? col.disableColumnMenu : true,
    }));
    const columnsWithActions = [
      ...columnsWithDisableMenu,
      ...(!withoutActionColumn ? [columnActions] : []),
    ];

    // Add dropdown column if rowDropdownIcon is provided
    const dropdownColumn = rowDropdownIcon
      ? ([{
        field: 'dropdown',
        headerName: '',
        width: 48,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params: any) => rowDropdownIcon(params.row),
        align: 'center',
        headerAlign: 'center',
      }] as GridColDef[])
      : [];

    useImperativeHandle(ref, () => ({
      reloadData: reloadData,
      deleteSelectedRows: deleteSelectedRows,
      getSelectedRowIds: getSelectedRowIds,
    }));

    if (!isNonEmptyString(baseUrl) || !isNonEmptyArray(columns)) {
      return null;
    }

    return (
      <S.CrudGridContainer>
        {!!header ? <S.CrudGridHeader>{header}</S.CrudGridHeader> : null}
        <Box sx={{ background: colors.white, padding: 3, borderRadius: '10px' }}>
          <Box sx={{ display: 'flex', mb: 2, alignItems: 'center', justifyContent: 'space-between' }}>
            {!hideSearchBar && (
              <TextField
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  onSearchChange(e.target.value);
                  // Notify the parent component, if it asked. No-op when
                  // no callback was provided — keeps existing consumers
                  // (Products, Dealers, etc.) unaffected.
                  setSearch?.(e.target.value);
                }}
                placeholder={searchPlaceHolder || "Search by item name or sku"}
                variant="outlined"
                size="small"
                sx={{
                  width: "25%",
                  background: "#fff",
                  borderRadius: "24px",
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '50px',
                    fontSize: '14px',
                    paddingRight: '8px',
                    py:1
                  },
                  '& .MuiInputAdornment-root': {
                    color: '#A0AEC0',
                  },
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            <Box sx={{ display: 'flex', gap: 2, ml: hideSearchBar ? 'auto' : 0 }}>
              {bulkImportButton && (
                <UploadFileInput
                  url={bulkImportButton.url}
                  text={bulkImportButton.text}
                  endIcon={bulkImportButton.endIcon}
                  onUploaded={bulkImportButton.onUploaded}
                  onError={bulkImportButton.onError}
                  sxButton={{
                    color: colors.black,
                    background: colors.white,
                    borderRadius: "12px",
                    padding: "12px",
                    fontFamily: 'PF DIN Text Pro'
                  }}
                />
              )}
              {filtersButton && filtersButton}
              {exportButton && (
                <Button
                  onClick={exportButton.onClick}
                  sx={{
                    color: colors.black,
                    background: colors.white,
                    borderRadius: "12px",
                    padding: "12px",
                    fontFamily: 'PF DIN Text Pro'
                  }}
                  endIcon={exportButton.isLoading ? <CircularProgress size={20} color="inherit" /> : exportButton.endIcon}
                  disabled={exportButton.isLoading}
                >
                  {exportButton.isLoading ? 'Exporting...' : exportButton.text}
                </Button>
              )}
            </Box>
          </Box>
          <Box>
            <DataGrid
              apiRef={apiRef}
              autoHeight
              rows={rows}
              columns={[...columnsWithActions, ...dropdownColumn]}
              editMode="row"
              loading={isLoading}
              pageSizeOptions={[pageSize]}
              paginationMode="server"
              paginationModel={paginationModel}
              rowCount={totalRowCount}
              onPaginationModelChange={onPaginationModelChange}
              filterMode="server"
              onFilterModelChange={onFilterModelChange}
              sortingMode="server"
              onSortModelChange={onSortModelChange}
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStop={handleRowEditStop}
              onRowClick={onRowClick}
              processRowUpdate={processRowUpdate}
              getRowId={getRowId}
              initialState={initialState}
              checkboxSelection={checkboxSelection}
              sx={DataGridDefaultStyle}
              slots={{
                toolbar: EditToolbar,
              }}
              slotProps={{
                toolbar: {
                  setRows,
                  setRowModesModel,
                  newRecord,
                  fieldToFocus,
                  newRecordId,
                  exportFn,
                },
              }}
            />
          </Box>
        </Box>
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => handleDeleteDialogClosed()}
          aria-labelledby="crud-grid-delete-confirm-dialog-title"
          aria-describedby="crud-grid-delete-confirm-dialog-description"
          sx={{
            '& .MuiDialog-paper': {
              px: 4,
              pt: 3,
              borderRadius:3
            }
          }}
        >
          <DialogTitle id="crud-grid-delete-confirm-dialog-title">
            <Typography sx={{ ...sxFont_18_500, color: colors.neutralGrey800 }}>
              Delete
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="crud-grid-delete-confirm-dialog-description">
              <Typography
                sx={{ ...sxFont_16_400, color: colors.neutralGrey700 }}
              >
                Are you sure?  <br />
                This action is not reversable!
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ m: "auto", mb: 2 }}>
            <Button onClick={() => handleDeleteDialogClosed()} variant="outlined" sx={{ ...sxButton, bgcolor: "transparent", color: colors.doveGray, borderRadius: "8px", px: 2 }}>
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteDialogClosed(true)}
              autoFocus
              sx={{ ...sxButton, bgcolor: colors.red, borderRadius: "8px", px: 2 }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        {error ? (
          <AlertDialog
            message={formatAxiosError(error)}
            title="Error"
            confirmText="Ok"
            severity="error"
          />
        ) : null}
      </S.CrudGridContainer>
    );
  }
);

CrudGrid.displayName = "CrudGrid";
export default CrudGrid;
